import ScrollToTop from "react-scroll-to-top";
import ChapterNav from './components/ChapterNav';
import MetricCards from './components/MetricCards';
import CompactImageCard from './components/CompactImageCard';
import ImageSubsection from './components/ImageSubsection';
import Footer from "../../toolbar/Footer";
import './ActivityTracking.css';
import images_BOE1 from "../../images/activity/BOE_1.png";
import images_BOE2 from "../../images/activity/BOE_2.png";
import images_Candriam1 from "../../images/activity/Candriam_1.png";
import images_Candriam2 from "../../images/activity/Candriam_2.png";
import images_Dashboards from "../../images/activity/Dashboards.png";
import images_Filtered from "../../images/activity/Filtered.png";
import images_Items from "../../images/activity/Items.png";
import images_MetricsOnTop from "../../images/activity/MetricsOnTop.png";
import images_Searches from "../../images/activity/Searches.png";
import images_Users from "../../images/activity/Users.png";
import images_Glossary1 from "../../images/activity/GlossaryA.png";
import images_Glossary2 from "../../images/activity/GlossaryB.png";
import images_ProjectManager1 from "../../images/activity/Project_managerA.png";
import images_ProjectManager3 from "../../images/activity/Project_managerC.png";
import images_SystemAdmin from "../../images/activity/System_admin.png";
import images_ActivityTrackingTemplate from "../../images/activity/ActivityTrackingTemplate.png";
import PageTransition from '../../pageTransition';

const CHAPTERS = [
    { id: 'ch-act-context', label: 'Context' },
    { id: 'ch-act-research', label: 'Research' },
    { id: 'ch-act-users', label: 'Users' },
    { id: 'ch-act-design', label: 'Design' },
    { id: 'ch-act-impact', label: 'Impact' },
];

const METRICS = [
    { value: 100, suffix: '%', label: 'Data Stays<br/>In-House' },
    { value: 0, suffix: '', label: 'Projects Without Analytics' },
    { symbol: '✓', label: 'Data-Driven<br/>Design Decisions' },
];

const ActivityTracking = (props) => {
    return (
        <>
        <PageTransition>
        <div className="mainBox">
            <div className="activityTracking project">
                <div className="project-inner">
                    <ImageSubsection
                        title="Activity Tracking"
                        banner={true}
                        blue={true}
                        images={[
                            { image: images_ActivityTrackingTemplate, width: '60rem', height: '35rem' },
                        ]}
                        description='Owned the full product lifecycle — from MRD and personas to shipping an enterprise analytics system<br/><br/><span style="opacity: 0.6">ROLE:</span> Lead Designer / Product Manager'
                    />

                    <div className="activity-spreads">
                        {/* ========================================= */}
                        {/* SPREAD 1: Context                         */}
                        {/* ========================================= */}
                        <div className="spread-act-context" id="ch-act-context">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">01</span>
                                <span className="spread-chapter-label">Context</span>
                                <p className="spread-chapter-desc">
                                    Enterprise customers are apprehensive about using commercial analytics tools due to data security concerns. This motivated us to build our own activity-tracking infrastructure — pre-built dashboards shipped with every Squirro app template, accessible only to authorized persons, with anonymized data when needed.
                                </p>
                            </div>

                            <div className="spread-cell--hero-img" style={{ gridArea: 'oi' }}>
                                <CompactImageCard
                                    image={images_MetricsOnTop}
                                    title="Activity Tracking — Overview Dashboard"
                                />
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 2: Research                        */}
                        {/* ========================================= */}
                        <div className="spread-act-research" id="ch-act-research">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">02</span>
                                <span className="spread-chapter-label">Research</span>
                                <p className="spread-chapter-desc">
                                    I studied competitor analytics tools — Google Analytics, Fathom, and Matomo — observing common patterns of information hierarchy. After thorough analysis and customer interviews, the most critical data clusters were Users, Dashboards, Searches, and Items.
                                </p>
                            </div>

                            <div className="spread-cell--card1" style={{ gridArea: 'c1' }}>
                                <CompactImageCard
                                    image={images_BOE1}
                                    title="Adoption at a Glance"
                                    caption="Top-level KPIs and time-series charts showing platform-wide adoption trends"
                                />
                            </div>
                            <div className="spread-cell--card2" style={{ gridArea: 'c2' }}>
                                <CompactImageCard
                                    image={images_BOE2}
                                    title="Session Drill-Down"
                                    caption="Drill-down view with per-user activity logs and session-level breakdowns"
                                />
                            </div>
                            <div className="spread-cell--card3" style={{ gridArea: 'c3' }}>
                                <CompactImageCard
                                    image={images_Candriam1}
                                    title="Team Engagement Map"
                                    caption="Summary dashboard with usage heatmaps and engagement metrics across teams"
                                />
                            </div>
                            <div className="spread-cell--card4" style={{ gridArea: 'c4' }}>
                                <CompactImageCard
                                    image={images_Candriam2}
                                    title="Search Pattern Analysis"
                                    caption="Filtered view showing search patterns and content interaction frequency"
                                />
                            </div>

                            <div className="spread-cell--workshop" style={{ gridArea: 'ws' }}>
                                <div className="spread-cell--workshop-text">
                                    <h3>Metrics Glossary</h3>
                                    <p>Product analytics tools track an exhaustive list of metrics, many irrelevant to our suite. I created a curated glossary so everyone spoke the same vocabulary and we invested only in measuring what mattered.</p>
                                </div>
                                <div className="spread-cell--workshop-thumbs">
                                    <CompactImageCard image={images_Glossary1} title="Glossary — Part A" />
                                    <CompactImageCard image={images_Glossary2} title="Glossary — Part B" />
                                </div>
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 3: Users                           */}
                        {/* ========================================= */}
                        <div className="spread-act-users" id="ch-act-users">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">03</span>
                                <span className="spread-chapter-label">Users</span>
                                <p className="spread-chapter-desc">
                                    As part of the MRD, I identified three user types and wrote user stories for each persona, validating them with customers to prioritize metrics for the MVP.
                                </p>
                            </div>

                            <div className="spread-cell--card1" style={{ gridArea: 'c1' }}>
                                <CompactImageCard
                                    image={images_ProjectManager1}
                                    title="Project Manager"
                                    caption="Monitors user activities with quantitative data to measure success and report to sponsors"
                                />
                            </div>
                            <div className="spread-cell--card2" style={{ gridArea: 'c2' }}>
                                <CompactImageCard
                                    image={images_SystemAdmin}
                                    title="System Administrator"
                                    caption="Needs quick access to log files for root cause analysis of backend service issues"
                                />
                            </div>
                            <div className="spread-cell--card3" style={{ gridArea: 'c3' }}>
                                <CompactImageCard
                                    image={images_ProjectManager3}
                                    title="Buyer"
                                    caption="Views adoption rate and value delivered over time to decide on renewals and subscriptions"
                                />
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 4: Design                          */}
                        {/* ========================================= */}
                        <div className="spread-act-design" id="ch-act-design">
                            <div className="spread-cell--strategy-text" style={{ gridArea: 'st' }}>
                                <span className="spread-chapter-number">04</span>
                                <span className="spread-chapter-label">Design</span>
                                <h3>Progressive Information Display</h3>
                                <p>
                                    The four data categories became top-level dashboard tabs. I designed three tiers of progressive information: top-level numbers for reporting, category-focused metrics for analysis, and log files for debugging. Sequential filtering lets users drill down from user groups to dashboards to items.
                                </p>
                            </div>

                            <div className="spread-cell--hero-img" style={{ gridArea: 'oi' }}>
                                <CompactImageCard
                                    image={images_MetricsOnTop}
                                    title="Overview — Tier 1 Reporting"
                                />
                            </div>

                            <div className="spread-cell--card1" style={{ gridArea: 'c1' }}>
                                <CompactImageCard
                                    image={images_Users}
                                    title="Users Dashboard"
                                />
                            </div>
                            <div className="spread-cell--card2" style={{ gridArea: 'c2' }}>
                                <CompactImageCard
                                    image={images_Dashboards}
                                    title="Dashboards"
                                />
                            </div>
                            <div className="spread-cell--card3" style={{ gridArea: 'c3' }}>
                                <CompactImageCard
                                    image={images_Searches}
                                    title="Searches"
                                />
                            </div>
                            <div className="spread-cell--card4" style={{ gridArea: 'c4' }}>
                                <CompactImageCard
                                    image={images_Items}
                                    title="Items"
                                />
                            </div>
                            <div className="spread-cell--card5" style={{ gridArea: 'c5' }}>
                                <CompactImageCard
                                    image={images_Filtered}
                                    title="Filtered Log View"
                                />
                            </div>
                        </div>

                        {/* ===== IMPACT METRICS ===== */}
                        <section id="ch-act-impact">
                            <MetricCards title="Impact" metrics={METRICS} />
                        </section>
                    </div>
                </div>
            </div>
            <Footer/>
        </div>
        </PageTransition>
        <ScrollToTop smooth className="scroll-to-top" />
        <ChapterNav chapters={CHAPTERS} />
        </>
    );
};

export default ActivityTracking;
